if (!customElements.get('bundle-builder-panel')) {
  customElements.define(
    'bundle-builder-panel',
    class BundleBuilderPanel extends HTMLElement {
      constructor() {
        super();
        this.items = [];
        this.storageKey = 'bundle_vat_declaration';
        this.itemsStorageKey = 'bundle_builder_items';
      }

      connectedCallback() {
        this.section = this.closest('.section-bundle-builder');
        this.currencySymbol = this.dataset.currencySymbol || '';
        this.emptyMessage = this.dataset.emptyMessage || '';
        this.cartAddUrl = this.dataset.cartAddUrl;
        this.cartUpdateUrl = this.dataset.cartUpdateUrl;
        this.cartUrl = this.dataset.cartUrl;
        this.items = this.loadPersistedItems();

        this.tiers = [1, 2, 3]
          .map((n) => ({
            number: n,
            qty: parseInt(this.dataset[`tier${n}Qty`], 10),
            discount: parseInt(this.dataset[`tier${n}Discount`], 10),
          }))
          .filter((tier) => !isNaN(tier.qty))
          .sort((a, b) => a.qty - b.qty);

        this.list = this.querySelector('[data-bundle-list]');
        this.emptyState = this.querySelector('[data-empty-state]');
        this.itemTemplate = this.querySelector('[data-bundle-item-template]');
        this.message = this.querySelector('[data-bundle-message]');
        this.minWarning = this.querySelector('[data-bundle-min-warning]');
        this.tierEls = [...this.section.querySelectorAll('[data-tier]')];
        this.addCartButton = this.querySelector('[data-bundle-add-cart]');
        this.addCartVatButton = this.querySelector('[data-bundle-add-cart-vat]');
        this.totalStandardEl = this.querySelector('[data-bundle-total-standard]');
        this.totalDiscountedEl = this.querySelector('[data-bundle-total-discounted]');
        this.totalVatEl = this.querySelector('[data-bundle-total-vat]');
        this.vatSavingsEl = this.querySelector('[data-bundle-vat-savings]');

        this.vatDeclarationDialog = this.querySelector('[data-vat-declaration-dialog]');
        this.vatDeclarationForm = this.querySelector('[data-vat-declaration-form]');

        this.mobileCount = this.querySelector('[data-bundle-mobile-count]');
        this.mobileToggle = this.querySelector('[data-bundle-toggle]');
        this.mobileToggleLabel = this.querySelector('[data-bundle-toggle-label]');

        this.setupFilters();
        this.setupAddToBundle();
        this.setupListInteractions();
        this.setupCartButtons();
        this.setupVatDialogs();
        this.setupMobileToggle();
        this.setupQuickView();

        this.render();
      }

      setupQuickView() {
        if (!this.section) return;

        this.quickViewDialog = this.section.querySelector('[data-quick-view-dialog]');
        this.quickViewContent = this.section.querySelector('[data-quick-view-content]');
        if (!this.quickViewDialog || !this.quickViewContent) return;

        this.quickViewSpinnerHTML = this.quickViewContent.innerHTML;

        this.section.querySelectorAll('.bundle-builder__grid .card-relish a[href]').forEach((link) => {
          link.addEventListener('click', (event) => {
            event.preventDefault();
            const addButton = link.closest('.card-relish')?.querySelector('[data-bundle-add]');
            this.openQuickView(link.href, addButton);
          });
        });

        this.quickViewDialog.querySelector('.close')?.addEventListener('click', () => this.quickViewDialog.close());
        this.quickViewDialog.addEventListener('click', (event) => {
          if (event.target === this.quickViewDialog) this.quickViewDialog.close();
        });
        this.quickViewDialog.addEventListener('close', () => {
          this.quickViewContent.innerHTML = this.quickViewSpinnerHTML;
        });
      }

      async openQuickView(url, addButton) {
        this.quickViewDialog.showModal();

        try {
          const response = await fetch(url);
          if (!response.ok) throw new Error(`Request failed with status ${response.status}`);
          const html = await response.text();
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const main = doc.querySelector('.section-main-product');

          if (main) {
            if (addButton) this.replaceAddToCartButtons(main, addButton);
            this.quickViewContent.innerHTML = '';
            this.quickViewContent.appendChild(main);
          } else {
            this.quickViewContent.innerHTML = '<p>Sorry, this product could not be loaded.</p>';
          }
        } catch (error) {
          this.quickViewContent.innerHTML = '<p>Sorry, this product could not be loaded.</p>';
        }
      }

      replaceAddToCartButtons(main, sourceButton) {
        const addToCart = main.querySelector('.add-to-cart');
        const addToCartVat = main.querySelector('.add-to-cart-vat-relief');
        const groupQuantityAdd = main.querySelector('.group-quantity-add');
        const bundleButton = this.createAddToBundleButton(sourceButton);

        if (addToCart) {
          addToCart.replaceWith(bundleButton);
          addToCartVat?.remove();
        } else if (groupQuantityAdd) {
          groupQuantityAdd.replaceWith(bundleButton);
        }
      }

      createAddToBundleButton(sourceButton) {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'product-form__submit button button--full-width button--primary bg-racing-green';
        button.innerHTML = '<span>Add to bundle</span>';
        button.addEventListener('click', () => {
          this.addItem(this.buildItemFromButton(sourceButton));
          this.quickViewDialog.close();
        });
        return button;
      }

      setupMobileToggle() {
        if (!this.mobileToggle) return;
        this.mobileToggle.addEventListener('click', () => {
          const expanded = this.dataset.expanded === 'true';
          this.dataset.expanded = expanded ? 'false' : 'true';
          this.mobileToggle.setAttribute('aria-expanded', expanded ? 'false' : 'true');
          this.mobileToggleLabel.textContent = expanded ? 'Show bundle' : 'Hide bundle';
        });
      }

      setupFilters() {
        if (!this.section) return;
        const pills = this.section.querySelectorAll('[data-filter-value]');
        const gridItems = this.section.querySelectorAll('.bundle-builder__grid-item');
        if (!pills.length || !gridItems.length) return;

        pills.forEach((pill) => {
          pill.addEventListener('click', () => {
            pills.forEach((p) => {
              p.classList.remove('is-active');
              p.setAttribute('aria-pressed', 'false');
            });
            pill.classList.add('is-active');
            pill.setAttribute('aria-pressed', 'true');

            const value = pill.dataset.filterValue;
            gridItems.forEach((item) => {
              const match = value === 'all' || item.dataset.productType === value;
              item.hidden = !match;
            });
          });
        });
      }

      buildItemFromButton(button) {
        return {
          id: button.dataset.productId,
          title: button.dataset.productTitle,
          image: button.dataset.productImage,
          variantId: button.dataset.variantId,
          price: parseInt(button.dataset.price, 10) || 0,
          hasVatRelief: button.dataset.hasVatRelief === 'true',
          vatVariantId: button.dataset.vatVariantId,
          vatPrice: parseInt(button.dataset.vatPrice, 10) || 0,
        };
      }

      setupAddToBundle() {
        if (!this.section) return;
        this.section.addEventListener('click', (event) => {
          const addButton = event.target.closest('[data-bundle-add]');
          if (addButton) {
            event.preventDefault();
            this.addItem(this.buildItemFromButton(addButton));
            return;
          }

          const increaseButton = event.target.closest('[data-bundle-increase]');
          if (increaseButton) {
            event.preventDefault();
            const id = increaseButton.closest('[data-bundle-added]').dataset.productId;
            this.changeQty(id, 1);
            return;
          }

          const decreaseButton = event.target.closest('[data-bundle-decrease]');
          if (decreaseButton) {
            event.preventDefault();
            const id = decreaseButton.closest('[data-bundle-added]').dataset.productId;
            this.changeQty(id, -1);
          }
        });
      }

      setupListInteractions() {
        this.list.addEventListener('click', (event) => {
          const row = event.target.closest('[data-bundle-item]');
          if (!row) return;
          const id = row.dataset.id;

          if (event.target.closest('[data-qty-increase]')) {
            this.changeQty(id, 1);
          } else if (event.target.closest('[data-qty-decrease]')) {
            this.changeQty(id, -1);
          } else if (event.target.closest('[data-item-remove]')) {
            this.removeItem(id);
          }
        });
      }

      setupCartButtons() {
        this.addCartButton.addEventListener('click', () => {
          if (!this.checkMinimumTier()) return;
          this.addToCart(false);
        });
        if (this.addCartVatButton) {
          this.addCartVatButton.addEventListener('click', () => {
            if (!this.checkMinimumTier()) return;
            const declaration = this.getStoredDeclaration();
            if (declaration && declaration.eligible) {
              this.addToCart(true);
            } else {
              this.showVatDeclaration();
            }
          });
        }
      }

      checkMinimumTier() {
        if (!this.minWarning) return true;
        const minTier = this.tiers[0];
        const totalQty = this.items.reduce((sum, item) => sum + item.qty, 0);
        const belowMinimum = Boolean(minTier) && totalQty < minTier.qty;
        this.minWarning.hidden = !belowMinimum;
        return !belowMinimum;
      }

      showVatDeclaration() {
        this.addCartVatButton.classList.add('hidden');
        this.vatDeclarationDialog.classList.remove('hidden');
      }

      hideVatDeclaration() {
        this.vatDeclarationDialog.classList.add('hidden');
        this.addCartVatButton.classList.remove('hidden');
      }

      setupVatDialogs() {
        if (!this.vatDeclarationDialog || !this.vatDeclarationForm) return;

        this.vatDeclarationDialog.querySelector('[data-vat-declaration-close]')?.addEventListener('click', () => {
          this.hideVatDeclaration();
        });

        const personalField = this.vatDeclarationForm.querySelector('[data-vat-field="personal"]');
        const charityField = this.vatDeclarationForm.querySelector('[data-vat-field="charity"]');
        const personalFields = this.vatDeclarationForm.querySelector('[data-vat-fields="personal"]');
        const charityFields = this.vatDeclarationForm.querySelector('[data-vat-fields="charity"]');
        const ineligible = this.vatDeclarationForm.querySelector('[data-vat-ineligible]');
        const submit = this.vatDeclarationForm.querySelector('[data-vat-declaration-submit]');

        const evaluate = () => {
          const formData = new FormData(this.vatDeclarationForm);
          const use = formData.get('vat_use');

          personalField.classList.toggle('hidden', use !== 'personal');
          charityField.classList.toggle('hidden', use !== 'charity');
          ineligible.classList.add('hidden');
          personalFields.classList.add('hidden');
          charityFields.classList.add('hidden');

          let eligible = false;
          let complete = false;

          if (use === 'professional') {
            ineligible.classList.remove('hidden');
          } else if (use === 'personal') {
            const hasDisability = formData.get('vat_has_disability');
            if (hasDisability === 'no') {
              ineligible.classList.remove('hidden');
            } else if (hasDisability === 'yes') {
              personalFields.classList.remove('hidden');
              eligible = true;
              complete = Boolean(formData.get('full_name'));
            }
          } else if (use === 'charity') {
            const confirmCharity = formData.get('vat_charity_confirm');
            if (confirmCharity === 'no') {
              ineligible.classList.remove('hidden');
            } else if (confirmCharity === 'yes') {
              charityFields.classList.remove('hidden');
              eligible = true;
              complete = Boolean(formData.get('charity_name'));
            }
          }

          submit.classList.toggle('hidden', !eligible);
          submit.disabled = !(eligible && complete);
          return { eligible, formData };
        };

        this.vatDeclarationForm.addEventListener('change', evaluate);
        this.vatDeclarationForm.addEventListener('input', evaluate);

        this.vatDeclarationForm.addEventListener('submit', (event) => {
          event.preventDefault();
          const { eligible, formData } = evaluate();
          if (!eligible || submit.disabled) return;

          const declaration = {
            eligible: true,
            full_name: formData.get('full_name') || '',
            condition: formData.get('condition') || '',
            charity_name: formData.get('charity_name') || '',
            charity_number: formData.get('charity_number') || '',
          };
          sessionStorage.setItem(this.storageKey, JSON.stringify(declaration));
          this.hideVatDeclaration();
          this.addToCart(true);
        });
      }

      getStoredDeclaration() {
        try {
          return JSON.parse(sessionStorage.getItem(this.storageKey));
        } catch (e) {
          return null;
        }
      }

      loadPersistedItems() {
        try {
          const raw = localStorage.getItem(this.itemsStorageKey);
          if (!raw) return [];
          const parsed = JSON.parse(raw);
          if (!Array.isArray(parsed)) return [];
          return parsed.filter((item) => item && item.id && item.variantId && item.qty > 0);
        } catch (e) {
          return [];
        }
      }

      persistItems() {
        try {
          if (this.items.length === 0) {
            localStorage.removeItem(this.itemsStorageKey);
          } else {
            localStorage.setItem(this.itemsStorageKey, JSON.stringify(this.items));
          }
        } catch (e) {
          // Storage unavailable (private browsing, quota, etc.) - fail silently.
        }
      }

      addItem(data) {
        const existing = this.items.find((item) => item.id === data.id);
        if (existing) {
          existing.qty += 1;
        } else {
          this.items.push({ ...data, qty: 1 });
        }
        this.render();
      }

      removeItem(id) {
        this.items = this.items.filter((item) => item.id !== id);
        this.render();
      }

      changeQty(id, delta) {
        const item = this.items.find((item) => item.id === id);
        if (!item) return;
        item.qty += delta;
        if (item.qty <= 0) {
          this.removeItem(id);
          return;
        }
        this.render();
      }

      formatMoney(cents) {
        return `${this.currencySymbol}${(cents / 100).toFixed(2)}`;
      }

      // Matches Shopify's automatic discount calculation, which rounds the
      // discount amount down to the nearest cent per unit before summing.
      applyTierDiscount(tier, getPrice) {
        return this.items.reduce((sum, item) => {
          const price = getPrice(item);
          const unitDiscount = tier ? Math.floor((price * tier.discount) / 100) : 0;
          return sum + (price - unitDiscount) * item.qty;
        }, 0);
      }

      render() {
        this.list.querySelectorAll('[data-bundle-item]').forEach((row) => row.remove());
        this.emptyState.hidden = this.items.length > 0;
        if (this.minWarning) this.minWarning.hidden = true;

        this.items.forEach((item) => {
          const row = this.itemTemplate.content.firstElementChild.cloneNode(true);
          row.dataset.id = item.id;
          row.querySelector('[data-item-image]').src = item.image;
          row.querySelector('[data-item-image]').alt = item.title;
          row.querySelector('[data-item-title]').textContent = item.title;
          row.querySelector('[data-item-qty]').textContent = item.qty;
          this.list.appendChild(row);
        });

        const totalQty = this.items.reduce((sum, item) => sum + item.qty, 0);
        const standardTotal = this.items.reduce((sum, item) => sum + item.price * item.qty, 0);
        const hasVatEligibleItem = this.items.some((item) => item.hasVatRelief);
        const vatTotal = this.items.reduce(
          (sum, item) => sum + (item.hasVatRelief ? item.vatPrice : item.price) * item.qty,
          0
        );

        let activeTier = null;
        let nextTier = null;
        this.tiers.forEach((tier) => {
          if (totalQty >= tier.qty) {
            activeTier = tier;
          } else if (!nextTier) {
            nextTier = tier;
          }
        });

        this.tierEls.forEach((el) => {
          const tier = this.tiers.find((t) => t.number === parseInt(el.dataset.tier, 10));
          el.dataset.active = tier && activeTier && tier.qty === activeTier.qty ? 'true' : 'false';
        });

        const discountedStandardTotal = this.applyTierDiscount(activeTier, (item) => item.price);
        const discountedVatTotal = this.applyTierDiscount(activeTier, (item) =>
          item.hasVatRelief ? item.vatPrice : item.price
        );

        if (this.items.length === 0) {
          this.message.textContent = this.emptyMessage;
        } else if (activeTier) {
          const tierSavings = standardTotal - discountedStandardTotal;
          this.message.textContent = `${activeTier.discount}% off applied - you’ve saved ${this.formatMoney(
            tierSavings
          )}!`;
        } else if (nextTier) {
          const remaining = nextTier.qty - totalQty;
          this.message.textContent = `Add ${remaining} more to unlock ${nextTier.discount}% off`;
        } else {
          this.message.textContent = this.emptyMessage;
        }

        if (activeTier) {
          this.totalStandardEl.innerHTML = `<s>${this.formatMoney(standardTotal)}</s>`;
          if (this.totalDiscountedEl) {
            this.totalDiscountedEl.hidden = false;
            this.totalDiscountedEl.textContent = this.formatMoney(discountedStandardTotal);
          }
        } else {
          this.totalStandardEl.textContent = this.formatMoney(standardTotal);
          if (this.totalDiscountedEl) {
            this.totalDiscountedEl.hidden = true;
            this.totalDiscountedEl.textContent = '';
          }
        }
        this.addCartButton.disabled = this.items.length === 0;

        if (this.addCartVatButton) {
          this.addCartVatButton.hidden = !hasVatEligibleItem;
          this.addCartVatButton.disabled = this.items.length === 0;
          this.totalVatEl.textContent = this.formatMoney(discountedVatTotal);
          const savings = discountedStandardTotal - discountedVatTotal;
          this.vatSavingsEl.textContent = savings > 0 ? `you save ${this.formatMoney(savings)}` : '';
        }

        if (this.mobileCount) {
          this.mobileCount.textContent = `${totalQty} item${totalQty === 1 ? '' : 's'} in bundle`;
        }
        if (this.items.length === 0 && this.dataset.expanded === 'true') {
          this.dataset.expanded = 'false';
          if (this.mobileToggle) {
            this.mobileToggle.setAttribute('aria-expanded', 'false');
            this.mobileToggleLabel.textContent = 'Show bundle';
          }
        }

        this.persistItems();
        this.syncCardStates();
      }

      syncCardStates() {
        if (!this.section) return;

        this.section.querySelectorAll('[data-bundle-added]').forEach((addedEl) => {
          const id = addedEl.dataset.productId;
          const addButton = addedEl.previousElementSibling;
          const item = this.items.find((item) => item.id === id);

          if (item) {
            if (addButton && addButton.matches('[data-bundle-add]')) addButton.hidden = true;
            addedEl.hidden = false;
            const textEl = addedEl.querySelector('[data-bundle-added-text]');
            textEl.textContent = textEl.dataset.addedTemplate.replace(/\d+/, item.qty);
          } else {
            if (addButton && addButton.matches('[data-bundle-add]')) addButton.hidden = false;
            addedEl.hidden = true;
          }
        });
      }

      async addToCart(useVatRelief) {
        if (this.items.length === 0) return;

        const button = useVatRelief ? this.addCartVatButton : this.addCartButton;
        button.classList.add('loading');
        button.setAttribute('aria-disabled', 'true');
        button.querySelector('.loading__spinner').classList.remove('hidden');

        // Hidden (underscore) line item property so the bundle discount Function only counts bundle lines.
        const bundleId = `bb-${Date.now()}`;
        const items = this.items.map((item) => ({
          id: useVatRelief && item.hasVatRelief ? item.vatVariantId : item.variantId,
          quantity: item.qty,
          properties: { _bundle: bundleId },
        }));

        try {
          const cartEl = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
          // Guard against an un-upgraded custom element (e.g. its constructor threw) -
          // querySelector still finds the tag, but methods like getSectionsToRender would be missing.
          const cart = cartEl && typeof cartEl.getSectionsToRender === 'function' ? cartEl : null;
          const body = { items };
          if (cart) {
            body.sections = cart.getSectionsToRender().map((section) => section.id);
            body.sections_url = window.location.pathname;
            cart.setActiveElement(document.activeElement);
          }

          const response = await fetch(this.cartAddUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(body),
          });
          let data = await response.json();

          if (!response.ok) {
            throw new Error(data.description || 'Unable to add bundle to cart');
          }

          const attributeBody = { attributes: { _pageFeature: 'BundleBuilder' } };
          if (cart) {
            attributeBody.sections = body.sections;
            attributeBody.sections_url = body.sections_url;
          }
          const attributeResponse = await fetch(this.cartUpdateUrl, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify(attributeBody),
          });
          if (attributeResponse.ok) {
            data = await attributeResponse.json();
          }

          this.items = [];
          this.render();

          document.dispatchEvent(new CustomEvent('bundle:cart-updated', { detail: data }));

          if (cart) {
            cart.renderContents(data);
          } else {
            window.location = this.cartUrl;
          }
        } catch (error) {
          console.error(error);
        } finally {
          button.classList.remove('loading');
          button.removeAttribute('aria-disabled');
          button.querySelector('.loading__spinner').classList.add('hidden');
        }
      }
    }
  );
}

if (!customElements.get('bundle-filter-drawer')) {
  customElements.define(
    'bundle-filter-drawer',
    class BundleFilterDrawer extends HTMLElement {
      connectedCallback() {
        this.panel = this.querySelector('.bundle-builder__filter-drawer-panel');
        this.trigger = document.querySelector(`[aria-controls="${this.id}"]`);

        this.querySelectorAll('[data-bundle-filter-close]').forEach((el) =>
          el.addEventListener('click', this.close.bind(this))
        );
        this.querySelectorAll('[data-filter-value]').forEach((el) =>
          el.addEventListener('click', this.close.bind(this))
        );
        this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
        if (this.trigger) this.trigger.addEventListener('click', this.open.bind(this));
      }

      open() {
        this.classList.add('active');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'true');
        document.body.classList.add('overflow-hidden');
        trapFocus(this.panel);
      }

      close() {
        this.classList.remove('active');
        if (this.trigger) this.trigger.setAttribute('aria-expanded', 'false');
        document.body.classList.remove('overflow-hidden');
        removeTrapFocus(this.trigger);
      }
    }
  );
}
